import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, downloadOwnerPdf } from "@/lib/api";
import { Switch } from "@/components/ui/switch";
import { TopNav } from "@/components/TopNav";
import DocumentCanvas from "@/components/DocumentCanvas";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  Copy, CheckCircle, Clock, Download, Certificate, ProhibitInset, ArrowLeft,
  PaperPlaneTilt, EnvelopeSimple, ShieldCheck, BellRinging, ListNumbers, XCircle, Eye, Stack, Spinner,
} from "@phosphor-icons/react";

const StatusBadge = ({ status }) => {
  const map = {
    completed: { label: "Completed", cls: "bg-success/10 text-success border-success/30" },
    sent: { label: "In Progress", cls: "bg-action/10 text-action border-action/30" },
    voided: { label: "Voided", cls: "bg-slate-100 text-slate-500 border-slate-300" },
    declined: { label: "Declined", cls: "bg-destructive/10 text-destructive border-destructive/30" },
  };
  const s = map[status] || map.sent;
  return <span className={`inline-flex items-center rounded-full border px-3 py-1 text-xs font-semibold uppercase tracking-wide ${s.cls}`}>{s.label}</span>;
};

export default function EnvelopeDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [env, setEnv] = useState(null);
  const [doc, setDoc] = useState(null);
  const [reminding, setReminding] = useState(false);
  const [showTplDialog, setShowTplDialog] = useState(false);
  const [tplName, setTplName] = useState("");
  const [savingTpl, setSavingTpl] = useState(false);
  const [dlBusy, setDlBusy] = useState(null);

  const toggleCert = async (v) => {
    setEnv((e) => ({ ...e, include_certificate: v }));
    try {
      await api.patch(`/envelopes/${id}/certificate-setting`, { include_certificate: v });
      toast.success(v ? "Signers' copies will include the audit certificate" : "Signers' copies will exclude the audit certificate");
    } catch {
      setEnv((e) => ({ ...e, include_certificate: !v }));
      toast.error("Could not update setting");
    }
  };

  const download = async (mode) => {
    setDlBusy(mode);
    try { await downloadOwnerPdf(id, mode); } catch { toast.error("Download failed"); } finally { setDlBusy(null); }
  };

  const saveAsTemplate = async () => {
    if (!tplName.trim()) { toast.error("Name your template"); return; }
    setSavingTpl(true);
    try {
      await api.post(`/envelopes/${id}/save-template`, { name: tplName.trim() });
      toast.success("Template saved — find it on your dashboard");
      setShowTplDialog(false);
      setTplName("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save template");
    } finally {
      setSavingTpl(false);
    }
  };

  const load = useCallback(async () => {
    const r = await api.get(`/envelopes/${id}`);
    setEnv(r.data);
    if (!doc) {
      const d = await api.get(`/documents/${r.data.document_id}`);
      setDoc(d.data);
    }
    return r.data.status;
  }, [id, doc]);

  useEffect(() => {
    load().catch(() => toast.error("Document not found"));
    const t = setInterval(() => {
      load().then((status) => {
        if (status && status !== "sent") clearInterval(t);
      }).catch(() => {});
    }, 6000);
    return () => clearInterval(t);
  }, [load]);

  if (!env) return <div className="min-h-screen camino-dark"><TopNav /><div className="py-24 text-center text-slate-400">Loading…</div></div>;

  const signers = env.recipients.filter((r) => r.role === "signer");
  const done = signers.filter((r) => r.status === "signed").length;
  const pct = signers.length ? Math.round((done / signers.length) * 100) : 0;

  const copyLink = (token, name) => {
    const link = `${window.location.origin}/sign/${token}`;
    navigator.clipboard.writeText(link);
    toast.success(`Signing link for ${name} copied`);
  };

  const voidEnvelope = async () => {
    try {
      const r = await api.post(`/envelopes/${id}/void`);
      setEnv(r.data);
      toast.success("Document voided");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not void");
    }
  };

  const sendReminder = async () => {
    setReminding(true);
    try {
      const r = await api.post(`/envelopes/${id}/remind`);
      if (r.data.email_configured) {
        toast.success(`Reminder emailed to ${r.data.emailed} pending signer(s)`);
      } else {
        toast.info(`Email isn't set up yet — reminder logged for ${r.data.reminded} signer(s). Copy their link to share now.`);
      }
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not send reminder");
    } finally {
      setReminding(false);
    }
  };

  const seq = env.signing_order === "sequential";
  const isActiveSigner = (r) => {
    if (r.role !== "signer" || r.status === "signed") return false;
    if (!seq) return true;
    return !env.recipients.some((o) => o.role === "signer" && (o.order ?? 0) < (r.order ?? 0) && o.status !== "signed");
  };

  return (
    <div className="min-h-screen camino-dark">
      <TopNav>
        <Button variant="ghost" onClick={() => navigate("/")} className="gap-2 text-slate-500 px-2 sm:px-3" data-testid="back-to-dashboard">
          <ArrowLeft size={16} /> <span className="hidden sm:inline">Dashboard</span>
        </Button>
      </TopNav>

      <main className="mx-auto max-w-6xl px-4 sm:px-6 py-10 grid lg:grid-cols-[1fr_360px] gap-8">
        {/* Left: header + document preview */}
        <div className="animate-fade-up min-w-0">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <div className="flex items-center gap-3">
                <StatusBadge status={env.status} />
              </div>
              <h1 className="mt-3 font-heading text-2xl sm:text-3xl font-bold tracking-tight text-navy leading-tight break-words">{env.title}</h1>
              <p className="mt-2 text-slate-500">{env.document_name}</p>
            </div>
          </div>

          {env.status === "declined" && env.declined_by && (
            <div className="mt-6 rounded-xl border border-destructive/30 bg-destructive/[0.05] p-4 flex items-start gap-3" data-testid="declined-alert">
              <XCircle size={22} weight="duotone" className="text-destructive shrink-0 mt-0.5" />
              <div>
                <div className="font-semibold text-destructive">Declined by {env.declined_by.name}</div>
                <div className="text-sm text-slate-600 mt-0.5">
                  {env.declined_by.reason ? `“${env.declined_by.reason}”` : "No reason provided."}
                </div>
              </div>
            </div>
          )}

          {doc && (
            <div className="mt-8 rounded-xl border border-slate-200 grid-canvas p-6 overflow-hidden">
              <DocumentCanvas docId={doc.id} pages={doc.pages} fields={env.fields} recipients={env.recipients} mode="view" maxWidth={720} />
            </div>
          )}
        </div>

        {/* Right: tracking sidebar */}
        <aside className="space-y-6">
          <div className="rounded-xl border border-slate-200 bg-white p-5 animate-fade-up">
            <div className="flex items-center justify-between">
              <span className="text-[11px] font-semibold uppercase tracking-[0.15em] text-slate-400">Progress</span>
              <span className="text-sm font-bold text-navy">{done}/{signers.length} signed</span>
            </div>
            <div className="mt-3 h-2 rounded-full bg-slate-100 overflow-hidden">
              <div className="h-full rounded-full transition-all duration-500" style={{ width: `${pct}%`, background: env.status === "completed" ? "#2A9D8F" : "#0052FF" }} />
            </div>
            <div className="mt-3 flex items-center gap-1.5 text-xs text-slate-500">
              <ListNumbers size={14} weight="duotone" className="text-navy" />
              {seq ? "Sequential — signers notified in order" : "Everyone can sign at once"}
            </div>
            {env.status === "sent" && env.auto_remind && (
              <div className="mt-1.5 flex items-center gap-1.5 text-xs text-slate-500">
                <BellRinging size={14} weight="duotone" className="text-action" />
                Auto-reminders every {env.remind_interval_days || 3} day{(env.remind_interval_days || 3) !== 1 ? "s" : ""}
              </div>
            )}
            {env.status === "sent" && (
              <Button onClick={sendReminder} disabled={reminding} data-testid="send-reminder-btn"
                className="mt-4 w-full rounded-full bg-action hover:bg-action/90 gap-2">
                <BellRinging size={16} weight="bold" /> {reminding ? "Sending…" : "Send reminder"}
              </Button>
            )}
          </div>

          {env.status === "completed" && (
            <div className="rounded-xl border border-success/30 bg-success/[0.05] p-5 animate-fade-up">
              <div className="flex items-center gap-2 text-success"><ShieldCheck size={20} weight="duotone" /><span className="font-semibold">Fully executed</span></div>
              <p className="mt-1 text-sm text-slate-500">All signers completed. Download the signed document and audit certificate.</p>
              <div className="mt-4 grid gap-2">
                <Button onClick={() => download("include")} disabled={!!dlBusy} className="w-full rounded-full bg-navy hover:bg-navy/90 gap-2" data-testid="download-with-cert-btn">
                  {dlBusy === "include" ? <Spinner size={16} className="animate-spin" /> : <Download size={16} />} Signed + Audit Certificate
                </Button>
                <Button onClick={() => download("exclude")} disabled={!!dlBusy} variant="outline" className="w-full rounded-full gap-2 text-navy" data-testid="download-signed-btn">
                  {dlBusy === "exclude" ? <Spinner size={16} className="animate-spin" /> : <Download size={16} />} Signed Document Only
                </Button>
                <Button onClick={() => download("only")} disabled={!!dlBusy} variant="ghost" className="w-full rounded-full gap-2 text-navy" data-testid="download-cert-btn">
                  {dlBusy === "only" ? <Spinner size={16} className="animate-spin" /> : <Certificate size={16} weight="duotone" />} Audit Certificate Only
                </Button>
              </div>
            </div>
          )}

          {["sent", "completed"].includes(env.status) && (
            <div className="rounded-xl border border-slate-200 bg-white p-5 animate-fade-up" data-testid="certificate-setting-card">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="text-sm font-semibold text-navy">Include audit certificate for signers</div>
                  <p className="mt-1 text-xs text-slate-500 leading-relaxed">
                    {env.status === "completed"
                      ? (env.include_certificate !== false
                        ? "Signers' future downloads include the audit certificate at the end. (The completion email was already sent.)"
                        : "Signers' future downloads are the signed document only. (The completion email was already sent.)")
                      : (env.include_certificate !== false
                        ? "Signers' signed copy (download and completion email) includes the audit certificate at the end."
                        : "Signers receive the signed document only, without the audit certificate.")}
                  </p>
                </div>
                <Switch checked={env.include_certificate !== false} onCheckedChange={toggleCert} data-testid="detail-include-certificate-toggle" />
              </div>
            </div>
          )}

          <div className="rounded-xl border border-slate-200 bg-white p-5 animate-fade-up">
            <span className="text-[11px] font-semibold uppercase tracking-[0.15em] text-slate-400">Recipients</span>
            <div className="mt-3 space-y-3">
              {env.recipients.map((r) => (
                <div key={r.id} className="flex items-start gap-3" data-testid={`recipient-status-${r.id}`}>
                  {seq && r.role === "signer" ? (
                    <span className="mt-0.5 w-5 h-5 rounded-full text-white text-[10px] font-bold flex items-center justify-center shrink-0" style={{ background: r.color }}>{(r.order ?? 0) + 1}</span>
                  ) : (
                    <span className="mt-1 w-2.5 h-2.5 rounded-full shrink-0" style={{ background: r.color }} />
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-navy truncate">{r.name}</span>
                      {r.role === "copy" ? (
                        <span className="text-[10px] font-semibold uppercase text-slate-400">Copy</span>
                      ) : r.status === "signed" ? (
                        <CheckCircle size={16} weight="fill" className="text-success shrink-0" />
                      ) : r.status === "declined" ? (
                        <XCircle size={16} weight="fill" className="text-destructive shrink-0" />
                      ) : r.viewed_at ? (
                        <Eye size={16} weight="fill" className="shrink-0" style={{ color: "#6AA0FF" }} />
                      ) : (
                        <Clock size={15} weight="duotone" className="text-slate-400 shrink-0" />
                      )}
                    </div>
                    <div className="text-xs text-slate-400 truncate">{r.email}</div>
                    {r.role === "signer" && r.status !== "signed" && r.status !== "declined" && (
                      r.viewed_at ? (
                        <div className="text-[11px] mt-0.5 flex items-center gap-1" style={{ color: "#6AA0FF" }} data-testid={`opened-${r.id}`}>
                          <Eye size={12} weight="fill" /> Opened {new Date(r.viewed_at).toLocaleString()}{r.view_count > 1 ? ` · ${r.view_count}×` : ""}
                        </div>
                      ) : (
                        <div className="text-[11px] text-slate-400 mt-0.5" data-testid={`not-opened-${r.id}`}>Not opened yet</div>
                      )
                    )}
                    {r.status === "declined" && (
                      <div className="text-[11px] text-destructive mt-0.5">Declined{r.decline_reason ? `: “${r.decline_reason}”` : ""}</div>
                    )}
                    {r.role === "signer" && r.status !== "signed" && env.status === "sent" && (
                      isActiveSigner(r)
                        ? <div className="text-[11px] font-semibold text-action mt-0.5">Their turn to sign</div>
                        : seq ? <div className="text-[11px] text-slate-400 mt-0.5">Waiting for earlier signer</div> : null
                    )}
                    {r.status === "signed" && r.signed_at && (
                      <div className="text-[11px] text-success mt-0.5">Signed {new Date(r.signed_at).toLocaleString()}</div>
                    )}
                    {r.role === "signer" && r.status !== "signed" && env.status === "sent" && (
                      <button onClick={() => copyLink(r.token, r.name)} data-testid={`copy-link-${r.id}`}
                        className="mt-1.5 inline-flex items-center gap-1.5 text-xs font-semibold text-action hover:underline">
                        <Copy size={13} /> Copy signing link
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="rounded-xl border border-slate-200 bg-white p-5 animate-fade-up">
            <span className="text-[11px] font-semibold uppercase tracking-[0.15em] text-slate-400">Audit Trail</span>
            <div className="mt-3 space-y-3">
              {(env.audit || []).map((a, i) => (
                <div key={i} className="flex gap-3 text-sm">
                  <div className="mt-1.5 w-1.5 h-1.5 rounded-full bg-navy shrink-0" />
                  <div>
                    <div className="text-navy">{a.detail}</div>
                    <div className="text-[11px] text-slate-400">{new Date(a.at).toLocaleString()}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <Button variant="outline" onClick={() => { setTplName(env.title); setShowTplDialog(true); }} data-testid="save-as-template-btn"
            className="w-full rounded-full gap-2 text-navy">
            <Stack size={16} weight="duotone" /> Save as template
          </Button>

          {env.status === "sent" && (
            <Button variant="ghost" onClick={voidEnvelope} data-testid="void-btn" className="w-full text-destructive hover:text-destructive hover:bg-destructive/5 gap-2">
              <ProhibitInset size={16} /> Void Document
            </Button>
          )}
        </aside>
      </main>

      <Dialog open={showTplDialog} onOpenChange={setShowTplDialog}>
        <DialogContent data-testid="save-template-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl tracking-tight flex items-center gap-2">
              <Stack size={22} weight="duotone" className="text-action" /> Save as template
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-500 -mt-2">Reuse this document, its title, recipients (names and emails), field layout and send settings to send new copies in seconds.</p>
          <Input value={tplName} onChange={(e) => setTplName(e.target.value)} placeholder="Template name" className="mt-2 h-11" data-testid="template-name-input" />
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowTplDialog(false)}>Cancel</Button>
            <Button onClick={saveAsTemplate} disabled={savingTpl} className="rounded-full bg-navy hover:bg-navy/90 gap-2" data-testid="confirm-save-template-btn">
              {savingTpl ? <Spinner size={16} className="animate-spin" /> : <Stack size={16} weight="bold" />} Save Template
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
